import React from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  MapPin,
  Heart,
  Compass,
  Camera,
  Users,
  Crown,
  Shield,
  Zap,
  Target,
  Route
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import Header from '../components/Header';
import Footer from '../components/Footer';
import FloatingActions from '../components/FloatingActions';
import SEOHead from '../components/SEOHead';

const AboutPage = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-purple-50">
      <SEOHead
        title="About Wanderloop - Mindful Walking & Cultural Discovery"
        description="Discover Los Angeles through thoughtfully curated walks that blend wellness, culture, and community. Your journey to mindful movement starts here."
      />
      <Header />

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-slate-900 via-blue-900 to-purple-900 text-white py-20 overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-20 left-10 w-32 h-32 bg-amber-400/10 rounded-full blur-xl"></div>
          <div className="absolute bottom-20 right-10 w-40 h-40 bg-orange-400/10 rounded-full blur-xl"></div>
        </div>

        <div className="relative max-w-6xl mx-auto px-4 text-center">
          {/* Logo mark */}
          <div className="mb-12">
            <div className="w-32 h-32 bg-gradient-to-r from-yellow-400 to-orange-500 rounded-full flex items-center justify-center mx-auto shadow-2xl">
              <div className="w-16 h-16 border-4 border-white rounded-full"></div>
            </div>
          </div>

          <h1 className="text-4xl md:text-6xl font-black mb-6">
            Built for People Who Walk Cities
          </h1>

          <p className="text-xl text-gray-300 mb-12 max-w-3xl mx-auto leading-relaxed">
            Wanderloop is a growing collection of curated urban walks, starting in Los Angeles — created by locals, for anyone curious enough to explore on foot.
          </p>
        </div>
      </section>

      {/* Mission & Values Section */}
      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            {/* Mission Content */}
            <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-2xl p-8">
              <h2 className="text-3xl font-black text-gray-900 mb-8">
                Our Mission: Know Your City on Foot
              </h2>

              <div className="space-y-6">
                {[
                  {
                    icon: <MapPin className="h-6 w-6" />,
                    title: "Scenic Beauty",
                    description: "Every walk chosen for natural beauty and photogenic moments",
                    color: "from-amber-400 to-orange-500"
                  },
                  {
                    icon: <Compass className="h-6 w-6" />,
                    title: "Cultural Heritage",
                    description: "Discover the stories and history that make each location unique",
                    color: "from-purple-500 to-blue-600"
                  },
                  {
                    icon: <Heart className="h-6 w-6" />,
                    title: "Mindful Wellness",
                    description: "Find peace and mental clarity in serene environments",
                    color: "from-amber-400 to-orange-500"
                  },
                  {
                    icon: <Users className="h-6 w-6" />,
                    title: "Community Connection",
                    description: "Build meaningful relationships through shared experiences",
                    color: "from-purple-500 to-blue-600"
                  }
                ].map((value, index) => (
                  <div key={index} className="flex items-start gap-4 group">
                    <div className={`w-12 h-12 bg-gradient-to-r ${value.color} rounded-xl flex items-center justify-center text-white shadow-lg group-hover:shadow-xl transition-all duration-300 transform group-hover:scale-110`}>
                      {value.icon}
                    </div>
                    <div className="flex-1">
                      <h3 className="font-bold text-lg text-gray-900 mb-1">
                        {value.title}
                      </h3>
                      <p className="text-gray-600">
                        {value.description}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Values Showcase */}
            <div>
              <Card className="bg-gradient-to-br from-amber-50 to-orange-50 border-2 border-amber-200 shadow-xl">
                <CardHeader>
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-gradient-to-r from-amber-400 to-orange-500 rounded-lg flex items-center justify-center">
                      <Compass className="h-5 w-5 text-white" />
                    </div>
                    <div>
                      <h3 className="font-bold text-xl text-gray-900">Why "Wanderloop"?</h3>
                      <Badge className="bg-amber-100 text-amber-700 text-xs">Our Philosophy</Badge>
                    </div>
                  </div>
                </CardHeader>

                <CardContent className="space-y-6">
                  {/* Philosophy explanation */}
                  <div className="space-y-4">
                    <div className="bg-white/80 backdrop-blur-sm rounded-lg p-4 border border-amber-200">
                      <div className="flex items-start gap-3">
                        <div className="w-3 h-3 bg-amber-500 rounded-full mt-2 flex-shrink-0"></div>
                        <div>
                          <h4 className="font-semibold text-gray-900 mb-1">Wander</h4>
                          <p className="text-sm text-gray-700">The spirit of open-minded discovery and curiosity</p>
                        </div>
                      </div>
                    </div>

                    <div className="bg-white/80 backdrop-blur-sm rounded-lg p-4 border border-purple-200">
                      <div className="flex items-start gap-3">
                        <div className="w-3 h-3 bg-purple-500 rounded-full mt-2 flex-shrink-0"></div>
                        <div>
                          <h4 className="font-semibold text-gray-900 mb-1">Loop</h4>
                          <p className="text-sm text-gray-700">The structure of returning to center, both physically and mentally</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Walking benefits */}
                  <div className="bg-gradient-to-r from-amber-100 to-orange-100 p-4 rounded-lg border-l-4 border-amber-400">
                    <div className="flex items-center gap-2 mb-2">
                      <Target className="h-5 w-5 text-amber-600" />
                      <span className="font-bold text-amber-800">The Art of Walking Well</span>
                    </div>
                    <p className="text-sm text-gray-700">Walking improves clarity, creativity, and mood. Every local walk can feel like an adventure when approached mindfully.</p>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 bg-gradient-to-br from-gray-50 to-blue-50">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-black text-gray-900 mb-4">
              Discover the City Around You — One Walk at a Time
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              From hidden cultural gems to scenic overlooks, discover the stories and beauty that make your city unique.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                icon: <Route className="h-8 w-8" />,
                title: "Curated Routes",
                description: "Handpicked walks featuring the best of LA's neighborhoods, each with detailed maps and insider tips.",
                color: "from-amber-400 to-orange-500"
              },
              {
                icon: <Camera className="h-8 w-8" />,
                title: "Cultural Discovery",
                description: "Uncover local art, architecture, and history through immersive walking experiences.",
                color: "from-amber-400 to-orange-500"
              },
              {
                icon: <Heart className="h-8 w-8" />,
                title: "Wellness Focus",
                description: "Transform your daily movement into mindful practice with gentle, accessible routes.",
                color: "from-amber-400 to-orange-500"
              }
            ].map((feature, index) => (
              <Card key={index} className="bg-white/80 backdrop-blur-sm border-0 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105">
                <CardContent className="p-8 text-center">
                  <div className={`w-16 h-16 bg-gradient-to-r ${feature.color} rounded-2xl flex items-center justify-center text-white shadow-lg mx-auto mb-6`}>
                    {feature.icon}
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-4">
                    {feature.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    {feature.description}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-blue-600 via-purple-600 to-blue-700 text-white relative overflow-hidden">
        {/* Background decoration */}
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHZpZXdCb3g9IjAgMCA0MCA0MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmZmZmYiIGZpbGwtb3BhY2l0eT0iMC4xIj48Y2lyY2xlIGN4PSIyMCIgY3k9IjIwIiByPSIzIi8+PC9nPjwvZz48L3N2Zz4=')]"></div>

        <div className="relative max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-4xl md:text-5xl font-black mb-8">
            Ready to Start Your Walking Journey?
          </h2>

          <p className="text-xl mb-12 text-blue-100 max-w-2xl mx-auto">
            Wanderloop is just getting started.
          </p>

          <div className="flex flex-col sm:flex-row gap-6 justify-center items-center mb-8">
            <Link to="/walks">
              <Button
                size="lg"
                className="bg-gradient-to-r from-yellow-400 to-orange-500 hover:from-yellow-500 hover:to-orange-600 text-black font-black px-12 py-4 text-xl shadow-2xl hover:shadow-yellow-500/25 transform hover:scale-105 transition-all duration-300"
              >
                <MapPin className="mr-3 h-6 w-6" />
                Explore Walks
                <ArrowRight className="ml-3 h-6 w-6" />
              </Button>
            </Link>

            <Link to="/membership">
              <Button
                size="lg"
                variant="outline"
                className="bg-transparent text-white border-white hover:bg-white/10 font-black px-12 py-4 text-xl border-2 transform hover:scale-105 transition-all duration-300"
              >
                <Crown className="mr-3 h-6 w-6" />
                Join Wanderloop+
              </Button>
            </Link>
          </div>

          <div className="flex items-center justify-center gap-8 text-sm text-blue-100">
            <div className="flex items-center gap-2">
              <Shield className="w-4 h-4" />
              <span>Free to explore</span>
            </div>
            <div className="flex items-center gap-2">
              <Zap className="w-4 h-4" />
              <span>New walks weekly</span>
            </div>
            <div className="flex items-center gap-2">
              <Users className="w-4 h-4" />
              <span>Growing community</span>
            </div>
          </div>
        </div>
      </section>

      {/* Floating Actions */}
      <FloatingActions />

      <Footer />
    </div>
  );
};

export default AboutPage;
